import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiAward, FiBarChart2, FiZap, FiUsers, FiGlobe, FiImage, FiCheck } = FiIcons;

const FeatureMockup = ({ type }) => {
  if (type === 'scheduling') {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm h-full flex flex-col">
        <div className="flex-1 space-y-4">
          <textarea 
            className="w-full h-32 p-4 border border-gray-200 rounded-lg resize-none text-gray-600" 
            defaultValue="Excited to announce our new product launch! 🚀 #productlaunch #startup" 
            readOnly
          ></textarea>
          <div className="flex gap-2">
            <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center text-pink-600 border-2 border-brand-teal">
              <SafeIcon icon={FiIcons.FiInstagram} />
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 border-2 border-brand-teal">
              <SafeIcon icon={FiIcons.FiFacebook} />
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-800 border-2 border-brand-teal">
              <SafeIcon icon={FiIcons.FiLinkedin} />
            </div>
          </div>
          <div className="h-40 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <SafeIcon icon={FiImage} className="mx-auto text-2xl mb-2"/>
              <span>Image Uploaded</span>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
          <div className="text-sm text-gray-500">Dec 15, 2024 at 10:00 AM</div>
          <div className="bg-brand-teal text-white px-4 py-2 rounded-lg text-sm font-bold">Schedule Post</div>
        </div>
      </div>
    );
  }
  
  if (type === 'campaign') {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm h-full flex gap-4">
        <div className="flex-1 space-y-4">
          <div className="text-lg font-bold text-brand-navy">Create New Campaign</div>
          <div className="space-y-2">
            <div className="text-xs font-bold text-gray-500 uppercase">Campaign Type</div>
            <div className="flex gap-2">
              <div className="flex-1 bg-brand-teal text-white text-xs py-2 text-center rounded">Contest</div>
              <div className="flex-1 bg-gray-100 text-gray-500 text-xs py-2 text-center rounded">Giveaway</div>
            </div>
          </div>
          <input type="text" className="w-full p-2 border rounded text-sm" value="Win a Free Course" readOnly/>
          <div className="bg-brand-yellow/10 p-3 rounded text-sm text-brand-navy">
            <div className="font-bold">247 entries so far</div>
          </div>
          <button className="w-full bg-brand-teal text-white py-2 rounded text-sm font-bold">Launch Campaign</button>
        </div>
        <div className="w-1/3 bg-gray-100 rounded-lg hidden sm:block"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-100 w-full h-full rounded-xl flex items-center justify-center text-gray-400">
      Screenshot Placeholder
    </div>
  );
};

const Features = () => {
  const features = [
    {
      icon: FiCalendar,
      title: "Unlimited Scheduling - No Caps, Ever",
      description: "Schedule 100 posts or 10. Post 5x/day or 1x/week. We don't care. Unlike Buffer's 10-post limit or Later's 30-post cap, Postifyr has ZERO restrictions. Post as much as you want.",
      type: 'scheduling'
    },
    {
      icon: FiAward,
      title: "Viral Campaign Builder (Normally $100+/month Extra)",
      description: "Run Instagram contests. Launch giveaways. Create referral campaigns. Collect UGC. Grow your audience organically. Most tools charge $100-300/month extra for this. We include it FREE in every plan.",
      type: 'campaign'
    },
    {
      icon: FiBarChart2,
      title: "Analytics That Actually Help",
      description: "See your best-performing posts. Know when your audience is online. Get AI-powered content suggestions. Track follower growth. No PhD required.",
      type: 'analytics'
    },
    {
      icon: FiZap,
      title: "Bulk Upload = 30 Posts in 3 Minutes",
      description: "Upload a CSV with 30 posts. We format everything. Add images. Schedule to optimal times. You're done. That's literally it.",
      type: 'bulk'
    },
    {
      icon: FiUsers,
      title: "Team Collaboration Included",
      description: "Invite team members. Set approval workflows. Manage multiple brands. No per-user fees like Hootsuite ($99/user). All our plans include 2-5 users.",
      type: 'team'
    },
    {
      icon: FiGlobe,
      title: "Works With 15+ Platforms",
      description: "Instagram • TikTok • Facebook • LinkedIn • Twitter • YouTube • Pinterest • Google Business Profile • Threads • Mastodon • And more coming",
      type: 'integrations'
    }
  ];

  return (
    <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-24"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
            Everything You Wish Your Current Tool Had
          </h2>
        </motion.div>

        {/* Features */}
        <div className="space-y-32">
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-16`}
            >
              {/* Visual Side */}
              <div className="flex-1 w-full relative">
                <div className={`absolute top-0 ${index % 2 === 0 ? '-left-20' : '-right-20'} w-72 h-72 bg-brand-teal/5 rounded-full blur-3xl -z-10`}></div>
                <div className="aspect-[3/2] w-full rounded-2xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden bg-white hover:transform hover:scale-[1.02] transition-transform duration-500">
                  {/* Mockup Logic */}
                  {feature.type === 'scheduling' && <FeatureMockup type="scheduling" />}
                  {feature.type === 'campaign' && <FeatureMockup type="campaign" />}
                  {feature.type === 'analytics' && (
                    <div className="p-6 h-full flex flex-col">
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div className="text-xs text-gray-500">Total Followers</div>
                          <div className="text-xl font-bold text-brand-navy">25.4K <span className="text-green-500 text-xs">+12%</span></div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div className="text-xs text-gray-500">Engagement</div>
                          <div className="text-xl font-bold text-brand-navy">4.2%</div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div className="text-xs text-gray-500">Posts</div>
                          <div className="text-xl font-bold text-brand-navy">47</div>
                        </div>
                      </div>
                      <div className="flex-1 bg-gradient-to-t from-brand-teal/10 to-transparent rounded-xl border-b border-brand-teal/20 relative">
                        <div className="absolute bottom-0 left-0 right-0 h-1/2 flex items-end justify-between px-6 pb-0">
                          {[30, 45, 35, 60, 50, 80, 70, 90, 65, 85].map((h, i) => (
                            <div key={i} className="w-full mx-1 bg-brand-teal rounded-t-sm opacity-80" style={{ height: `${h}%` }}></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {feature.type === 'bulk' && (
                    <div className="p-6 h-full flex flex-col">
                      <div className="font-bold text-brand-navy mb-4 border-b pb-2">Bulk Upload Posts</div>
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50 mb-4">
                        <div className="text-4xl mb-2">📄</div>
                        <div className="text-sm font-medium text-brand-teal">Click to upload CSV</div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs font-bold text-gray-500">
                          <span>Processing 30 posts...</span>
                          <span>100%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-teal w-full"></div>
                        </div>
                      </div>
                      <button className="mt-4 w-full bg-brand-teal text-white py-3 rounded-lg font-bold">Import All</button>
                    </div>
                  )}
                  {feature.type === 'team' && (
                    <div className="p-6 h-full flex flex-col">
                      <div className="font-bold text-brand-navy mb-4">Team Members</div>
                      <div className="space-y-4">
                        {['Sarah Martinez (Admin)', 'Marcus Johnson (Editor)', 'Zoe Chen (Viewer)'].map((name, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-brand-navy/10 flex items-center justify-center font-bold text-brand-navy">{name[0]}</div>
                              <span className="text-sm font-medium text-gray-700">{name}</span>
                            </div>
                            <div className="text-gray-400 text-xs">Edit</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="text-xs font-bold text-gray-400 mb-2 uppercase">Approval Workflow</div>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Draft</span>
                          <span>→</span>
                          <span className="text-brand-teal font-bold">Review</span>
                          <span>→</span>
                          <span>Approved</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {feature.type === 'integrations' && (
                    <div className="p-6 h-full flex flex-col">
                      <div className="font-bold text-brand-navy mb-4">Connected Platforms</div>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { n: 'Instagram', i: <SafeIcon icon={FiIcons.FiInstagram}/>, c: 'text-pink-600' },
                          { n: 'LinkedIn', i: <SafeIcon icon={FiIcons.FiLinkedin}/>, c: 'text-blue-700' },
                          { n: 'TikTok', i: <SafeIcon icon={FiIcons.FiVideo}/>, c: 'text-black' },
                          { n: 'Facebook', i: <SafeIcon icon={FiIcons.FiFacebook}/>, c: 'text-blue-600' },
                          { n: 'Twitter', i: <SafeIcon icon={FiIcons.FiTwitter}/>, c: 'text-sky-500' },
                          { n: 'YouTube', i: <SafeIcon icon={FiIcons.FiYoutube}/>, c: 'text-red-600' }
                        ].map((p, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg shadow-sm">
                            <div className={`text-xl ${p.c}`}>{p.i}</div>
                            <div className="flex-1">
                              <div className="text-xs font-bold text-gray-700">{p.n}</div>
                              <div className="text-[10px] text-gray-400">Connected</div>
                            </div>
                            <div className="text-green-500 text-lg"><SafeIcon icon={FiCheck} /></div>
                          </div>
                        ))}
                      </div>
                      <button className="mt-4 w-full border-2 border-dashed border-brand-teal text-brand-teal py-3 rounded-lg font-bold flex items-center justify-center gap-2">
                        + Add Platform
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Side */}
              <div className="flex-1 space-y-6">
                <SafeIcon icon={feature.icon} className="text-6xl text-brand-teal mb-4" />
                <h3 className="text-3xl md:text-4xl font-bold text-brand-navy leading-tight">
                  {feature.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Break */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-32 bg-brand-teal rounded-3xl p-12 text-center text-white relative overflow-hidden shadow-2xl shadow-brand-teal/40"
        >
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold mb-8">
              Ready to save 10+ hours per week?
            </h3>
            <a 
              href="https://dashboard.postifyr.com/rsa176125959b2e527cd97c7b79622c6fdb?ref=features" 
              className="inline-block bg-brand-yellow text-brand-navy text-xl font-bold px-12 py-5 rounded-xl hover:bg-white hover:scale-105 transition-all shadow-lg"
            >
              Start Free Trial →
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;